import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { UnmatchedOrderItem } from '@/supabase/inventory/types/inventory';

interface UnmatchedItemsPanelProps {
  unmatched: UnmatchedOrderItem[];
  unmatchedTotal: number;
  onRegister: (item: UnmatchedOrderItem) => void;
  registeringNorms: Set<string>;
}

/**
 * 미매칭 주문 상품 패널(PR3 Task4, dumb). 윈도우 내 주문(상태 무관 — overview RPC v4) 중 활성
 * 재고 마스터에 없는 상품명을 등록할 수 있게 한다 — '재고로 등록' 클릭 시 onRegister가 현재고
 * 입력 다이얼로그를 연다(오너 피드백 2026-07-13, 즉시 등록 아님). 입금 완료된 주문의 상품도
 * 등록 대상으로 남는다(입금 처리 부지런한 셀러의 패널이 늘 비던 v3 시맨틱 갭 해소).
 *
 * unmatched가 비어 있으면 패널 자체를 렌더하지 않는다(null) — Inventory.tsx는 이 계약을 그대로
 * 믿고 조건 없이 렌더한다. 등록 pending 상태(registeringNorms)는 부모가 소유·전달만 하고, 이
 * 컴포넌트는 onRegister 호출과 pending 표시만 담당한다(memo 불필요 — dumb 컴포넌트).
 *
 * registeringNorms.size > 0이면(다른 행이 등록 중이어도) 모든 등록 버튼을 비활성화한다 —
 * createItem은 useInventoryActions 안에서 단일 isSubmittingRef로 직렬화되므로, 다른 행을
 * 동시에 클릭해도 어차피 뒤 호출은 토스트 없이 조용히 무시된다. 여기서 미리 막아 "눌렀는데
 * 아무 반응 없음"처럼 보이는 걸 방지한다.
 */
export function UnmatchedItemsPanel({
  unmatched,
  unmatchedTotal,
  onRegister,
  registeringNorms,
}: UnmatchedItemsPanelProps) {
  // 기본 접힘(오너 2026-07-17) — 설명문이 헤더 행에 인라인된 뒤로는 접혀 있어도
  // 타이틀+카운트+설명으로 맥락이 전달되고, 대량 셀러의 패널이 재고 테이블을 밀어내지 않는다.
  const [isOpen, setIsOpen] = useState(false);

  if (unmatched.length === 0) return null;

  const hiddenCount = unmatchedTotal - unmatched.length;
  const isAnyRegistering = registeringNorms.size > 0;

  return (
    // '작업 필요' 시맨틱(UI 폴리시 3차) — 목록 카드와 같은 흰 카드 톤이라 두 구획이 한
    // 덩어리로 읽히던 것을 amber 틴트로 분리(카운트 뱃지 amber와 일관).
    <div className="rounded-2xl bg-amber-50/50 shadow-sm ring-1 ring-amber-200/70">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
        aria-expanded={isOpen}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {/* 타이틀 '재고 등록 추천 상품'(오너 확정 2026-07-17) — '미매칭'은 시스템 내부
              관점 용어라 교체. '추천'은 입금매칭 추천 후보와 같은 "시스템이 골라줌" 어휘. */}
          <span className="shrink-0 text-sm font-semibold text-slate-900">재고 등록 추천 상품</span>
          <Badge variant="secondary" className="shrink-0 bg-amber-100 text-amber-800 hover:bg-amber-100">
            {unmatchedTotal.toLocaleString('ko-KR')}
          </Badge>
          {/* 설명문은 타이틀과 같은 행(오너 2026-07-17) — 접힌 상태에서도 맥락이 보이고
              펼치면 리스트가 타이틀에 바로 붙는다. 좁은 화면(<sm)은 숨김, 넘치면 truncate
              (min-w-0 + flex-1이 truncate 성립 조건). 소급 차감 뉘앙스는 등록 다이얼로그
              캡션이 행동 시점에 안내. */}
          <span className="hidden min-w-0 truncate pl-1 text-xs font-normal text-slate-400 sm:inline">
            최근 정산서에서 찾은 상품입니다. 등록하면 같은 이름의 주문이 예약·차감에 자동
            반영됩니다.
          </span>
        </span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        )}
      </button>

      {isOpen && (
        <div className="border-t border-slate-100 px-4 pb-4 pt-3">
          {/* 설명문은 헤더 행으로 이동(오너 2026-07-17) — 본문은 리스트만. pt-3은 설명 <p>가
              지니던 상단 여백 승계. */}
          {/* 유계 스크롤(오너 2026-07-13): 대량 셀러는 미매칭이 수십 건이라 전부 펼치면 패널이
              페이지를 삼킨다 — 서버가 최근 주문 우선(overview RPC v4)으로 내려주므로 상단
              ~4행이 곧 지금 처리할 것들이고, 나머지는 이 안에서 스크롤. max-h는 행(~66px)
              4.5개 — 반 행이 걸쳐 보여 "더 있음"이 스크롤바 없이도 읽히는 높이. */}
          <ul className="max-h-[300px] space-y-2 overflow-y-auto pr-1">
            {unmatched.map((item) => {
              const isRegistering = registeringNorms.has(item.normName);
              return (
                <li
                  key={item.normName}
                  className="flex flex-col gap-2 rounded-xl border border-amber-100 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900" title={item.name}>
                      {item.name}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      수량 {item.qty.toLocaleString('ko-KR')}개 · 주문 {item.orderCount.toLocaleString('ko-KR')}건 ·
                      최근 {item.lastOrderDate} · {item.price.toLocaleString('ko-KR')}원
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    disabled={isAnyRegistering}
                    onClick={() => onRegister(item)}
                  >
                    {isRegistering ? '등록 중' : '재고로 등록'}
                  </Button>
                </li>
              );
            })}
          </ul>
          {hiddenCount > 0 && (
            <p className="break-keep pt-3 text-xs text-slate-400">
              외 {hiddenCount.toLocaleString('ko-KR')}건 · 등록하면 이어서 표시됩니다
            </p>
          )}
        </div>
      )}
    </div>
  );
}
